<template lang="pug">
    .filters-container
        button.drawer-button(@click="openDrawer")
            .filter-count {{ filterCount }}
            span Filters
            span.filter-clear-icon(@click.stop="clearFilters") &times;

        .drawer-backdrop(v-if="isDrawerOpen" @click="closeDrawer")

        .drawer(:class="{ 'drawer-open': isDrawerOpen }")
            .drawer-header
                h3 Filters
                button.close-button(@click="closeDrawer") &times;
            .search-area(@click="focusSearchField")
                .search-container
                    input.search-field(
                        placeholder="Search by section name"
                        v-model="searchQuery"
                        ref="search"
                    )
                    img.search-icon(
                        v-if="!searchQuery"
                        src="assets/images/search.svg" width="16" height="16")
                    span.clear-icon(v-if="searchQuery" @click="clearSearch")
            .drawer-content
                .filter-group(v-for="filter in filteredFilters" :key="filter.id" v-if="filter.show")
                    .filter-group-header(@click="toggleFilterGroup(filter.id)")
                        .filter-group-header-content
                            .accordion-icon(:class="{ 'open': filter.open }")
                                i.fa.fa-angle-right
                            h4 {{ filter.name }} {{ showFilterCount(filter) ? "(" + showFilterCount(filter) + ")" : "" }}
                        .filter-group-header-actions
                            i.fa.fa-times.filter-group-header-clear-icon(@click.stop="clearFilterGroup(filter.id, filter.type)")
                    .filter-group-content(v-show="filter.open")
                        button-filter(
                            v-if="filter.type === 'button'" 
                            :entries="filter.entries"
                            :query-key="filter.id"
                            :only-entries="filter.onlyEntries"
                            @item-click="handleUpdateButtonFilters"
                        )
                        checkbox-filter(
                            v-if="filter.type === 'checkbox'"
                            :ref="`checkbox-filter-${filter.id}`"
                            :data="filter.entries"
                            :filter-id="filter.id"
                            :tenant="tenant"
                            :has-search="filter.search"
                            :selected-items="filter.selectedItems"
                            @selection-change="handleUpdateCheckboxFilters"
                        )
                        radio-btn-filter(
                            v-if="filter.type === 'radio'"
                            :ref="`radio-filter-${filter.id}`"
                            :data="filter.entries"
                            :filter-id="filter.id"
                            :tenant="tenant"
                            :has-search="filter.search"
                            :selected-items="filter.selectedItems"
                            @selection-change="handleUpdateRadioBtnFilters"
                        )
                .filter-group(v-if="noResultsFound")
                    | No results found
                .drawer-footer
                    button.pxl-button.apply-button(@click="applyFilters" :disabled="disableActionButton") Apply
                    button.clear-button(@click="clearFilters" :disabled="disableActionButton") Clear All

</template>

<script>
import { ButtonFilter, CheckboxFilter, RadioBtnFilter } from "./components";
import {
  buttonFilterHelper,
  checkboxFilterHelper,
  radioBtnFilterHelper,
  getListOfCategoriesByIds,
} from "./helpers";
import { objDeepClone, handleActionError } from "@/utils";
import { categories, stateRefs, events } from "@/constants";
const { MAIN, SUB } = categories[stateRefs.category].types;
const { LIVE, UPCOMING, INITIALIZING, VOD, DELETED, MOVING_TO_VOD } =
  events[stateRefs.event].statuses;

export default {
  name: "Filters",
  components: {
    ButtonFilter,
    CheckboxFilter,
    RadioBtnFilter,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    tabIndex: {
      type: Number,
    },
    page: {
      type: String,
    },
    tenant: {
      type: String,
    },
    withCategories: {
      type: Boolean,
      default: false,
    },
    getFiltersToRemove: {
      type: Function,
      default: null,
    },
    startDate: {
      type: Number,
      default: null,
    },
    endDate: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      isDrawerOpen: false,
      filters: [],
      isHandleUpdate: false,
      searchQuery: "",

      selectedMainCategories: [],
      selectedSubCategories: [],

      previousFiltersCount: 0,
    };
  },
  computed: {
    filterCount() {
      return this.filters.reduce((acc, filter) => {
        if (filter.type === "button") {
          return acc + buttonFilterHelper.count(filter);
        }

        if (filter.type === "checkbox") {
          return acc + checkboxFilterHelper.count(filter);
        }

        if (filter.type === "radio") {
          return acc + radioBtnFilterHelper.count(filter);
        }

        return acc;
      }, 0);
    },
    filteredFilters() {
      return this.filters.filter(
        (filter) =>
          filter.show && filter.name.toLowerCase().includes(this.searchQuery.toLowerCase().trim()),
      );
    },
    noResultsFound() {
      return !this.filteredFilters.length && this.searchQuery;
    },
    disableActionButton() {
      return this.filterCount === 0 && this.filterCount === this.previousFiltersCount;
    },
  },
  methods: {
    openDrawer() {
      this.isDrawerOpen = true;
      this.$store.commit("temp/setShowBottomBar", false);
    },
    closeDrawer() {
      this.isDrawerOpen = false;
      this.searchQuery = "";
      this.updateFilters(this.$route.query);
      this.$store.commit("temp/setShowBottomBar", true);
    },
    toggleFilterGroup(filterId) {
      this.filters = this.filters.map((filter) => {
        if (filter.id === filterId) {
          return { ...filter, open: !filter.open };
        }

        return filter;
      });
    },
    updateFilters(query) {
      this.filters = this.filters.map((filter) => {
        if (filter.type === "button") {
          return buttonFilterHelper.update(filter, query);
        }

        const categories = this.withCategories
          ? {
              selectedMainCategories: this.selectedMainCategories,
              selectedSubCategories: this.selectedSubCategories,
            }
          : {};

        if (filter.type === "checkbox") {
          return checkboxFilterHelper.update(filter, query, categories);
        }

        if (filter.type === "radio") {
          return radioBtnFilterHelper.update(filter, query);
        }

        return filter;
      });
    },
    handleUpdateButtonFilters(item, queryKey) {
      this.filters = this.filters.map((filter) => {
        if (filter.id === queryKey) {
          return buttonFilterHelper.handleUpdate(filter, item);
        }

        return filter;
      });
    },
    handleUpdateCheckboxFilters({ filterId, selectedItems }) {
      if (filterId === "main-categories") {
        this.selectedMainCategories = selectedItems;
      }

      if (filterId === "sub-categories") {
        this.selectedSubCategories = selectedItems;
      }

      this.filters = this.filters.map((filter) => {
        if (filter.id === filterId) {
          return checkboxFilterHelper.handleSelectionChange(filter, { selectedItems });
        }

        return filter;
      });
    },
    handleUpdateRadioBtnFilters({ filterId, selectedItems }) {
      this.filters = this.filters.map((filter) => {
        if (filter.id === filterId) {
          return radioBtnFilterHelper.handleSelectionChange(filter, { selectedItems });
        }

        return filter;
      });
    },
    applyFilters() {
      let queryUpdates = {};
      let categories = new Set();

      this.filters.forEach((filter) => {
        if (filter.type === "button") {
          queryUpdates = {
            ...queryUpdates,
            ...buttonFilterHelper.getUpdatedQuery(filter, this.tabIndex),
          };
        }

        if (filter.type === "checkbox") {
          const updatedQuery = checkboxFilterHelper.getUpdatedQuery(filter);

          if (filter.queryKey && filter.selectedItems.length > 0) {
            categories = new Set([...categories, ...filter.selectedItems]);
          }

          queryUpdates = {
            ...queryUpdates,
            ...updatedQuery,
          };
        }

        if (filter.type === "radio") {
          const updatedQuery = radioBtnFilterHelper.getUpdatedQuery(filter);

          queryUpdates = {
            ...queryUpdates,
            ...updatedQuery,
          };
        }
      });

      if (categories.size > 0) {
        queryUpdates.categories = Array.from(categories).join(",");

        delete queryUpdates["main-categories"];
        delete queryUpdates["sub-categories"];
      } else {
        queryUpdates.categories = undefined;
      }

      const statusMatches =
        queryUpdates.status &&
        [LIVE, UPCOMING, INITIALIZING].some((status) => queryUpdates.status.includes(status));

      if (this.page === "Events" && statusMatches && this.getFiltersToRemove) {
        const filtersToRemove = this.getFiltersToRemove();

        queryUpdates = {
          ...queryUpdates,
          ...filtersToRemove,
          sort: "startDateTime",
        };
      }

      if (this.page === "Events" && [VOD, DELETED, MOVING_TO_VOD].includes(queryUpdates.status)) {
        queryUpdates = {
          ...queryUpdates,
          startDate: this.$route.query.startDate || this.startDate,
          endDate: this.$route.query.endDate || this.endDate,
          sort: "-startDateTime",
        };
      }

      this.isHandleUpdate = true;
      this.$updateQuery(queryUpdates);
      this.searchQuery = "";
      this.isDrawerOpen = false;
      this.$store.commit("temp/setShowBottomBar", true);
      this.previousFiltersCount = this.filterCount;
    },
    clearFilters() {
      this.filters = this.filters.map((filter) => {
        if (filter.type === "button") {
          return buttonFilterHelper.clear(filter);
        }

        if (filter.type === "checkbox") {
          return checkboxFilterHelper.clear(filter);
        }

        if (filter.type === "radio") {
          return radioBtnFilterHelper.clear(filter);
        }

        return filter;
      });
      this.selectedMainCategories = [];
      this.selectedSubCategories = [];
      this.applyFilters();
    },
    clearFilterGroup(groupId, type) {
      if (type === "button") {
        this.filters = buttonFilterHelper.clearGroup(this.filters, groupId);
      }

      if (type === "checkbox") {
        this.filters = checkboxFilterHelper.clearGroup(this.filters, groupId);
      }

      if (type === "radio") {
        this.filters = radioBtnFilterHelper.clearGroup(this.filters, groupId);
      }
    },
    async getMissedCategories() {
      const queryCategoriesIds = this.$route.query.categories.split(",");

      return getListOfCategoriesByIds(queryCategoriesIds)
        .then((response) => {
          response.items?.forEach((category) => {
            const { type } = category.attributes;

            if (type === MAIN) {
              this.selectedMainCategories.push(category.id);
            } else if (type === SUB) {
              this.selectedSubCategories.push(category.id);
            }
          });
        })
        .catch((error) => handleActionError(error, this, "getting categories"));
    },
    showFilterCount(filter) {
      if (filter.type === "button") {
        return buttonFilterHelper.count(filter);
      }

      if (filter.type === "checkbox" || filter.type === "radio") {
        return filter.selectedItems.length;
      }
    },
    clearSearch() {
      this.searchQuery = "";
    },
    focusSearchField() {
      this.$refs.search.focus();
    },
  },
  watch: {
    $route: {
      handler(route) {
        if (!this.isHandleUpdate) {
          if (!route.query.categories) {
            this.selectedMainCategories = [];
            this.selectedSubCategories = [];
          }

          this.updateFilters(route.query);
          this.previousFiltersCount = this.filterCount;
        }

        this.isHandleUpdate = false;
      },
    },
    data: {
      async handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.filters = objDeepClone(newVal);
          this.updateFilters(this.$route.query);
        }
      },
    },
  },
  async created() {
    if (this.withCategories && this.$route.query.categories) {
      await this.getMissedCategories();
    }

    this.filters = objDeepClone(this.data);
    this.updateFilters(this.$route.query);
    this.previousFiltersCount = this.filterCount;
  },
  beforeDestroy() {
    this.$store.commit("temp/setShowBottomBar", true);
  },
};
</script>

<style lang="stylus" scoped>
.filters-container
  position relative

.drawer-button
  height 34px
  width 118px
  background #fff
  border 1px solid button-background-color
  border-radius 3px
  cursor pointer
  display flex
  align-items center
  justify-content center
  gap 8px
  font-size 14px
  font-weight 500
  line-height 1
  color button-background-color
  transition all 0.2s ease
  box-sizing border-box

  .filter-count
    background button-background-color
    color #fff
    font-size 10px
    padding 0
    border-radius 50%
    width 18px
    height 18px
    display flex
    flex-shrink 0
    align-items center
    justify-content center
    font-weight 600
    line-height 1
    text-align center
    box-sizing border-box

  .filter-clear-icon
    font-size 26px
    color button-background-color

    &:hover
      color #666

.drawer-backdrop
  position fixed
  top 0
  left 0
  width 100vw
  height 100vh
  background rgba(0, 0, 0, 0.5)
  z-index 999

.drawer
  position fixed
  top 0
  right -360px
  width 360px
  height 100vh
  background #fff
  box-shadow 4px 4px 26px 0px #00000040;
  transition right 0.3s ease
  z-index 1000

  &.drawer-open
    right 0

.drawer-header
  padding 6px 16px
  border-bottom 1px solid #e0e0e0
  display flex
  justify-content space-between
  align-items center
  height 70px

  h3
    margin 0
    font-size 24px
    line-height 1

  h4
    margin 0
    font-size 16px
    line-height 1
    font-weight 600

.close-button
  background none
  border none
  font-size 24px
  font-weight 600
  cursor pointer
  color button-background-color
  padding 0
  height 24px
  width 28px
  display flex
  align-items center
  justify-content center

  &:hover
    color #333

.drawer-content
  padding 0 16px 16px 16px
  max-height calc(100vh - 175px)
  overflow-y auto
  &::-webkit-scrollbar
      width 6px
  &::-webkit-scrollbar-thumb
    border-radius 10px
    width 6px
    height auto
    background-color #d8d8d8

.filter-group
  padding 10px 0
  border-top 1px solid #e0e0e0
  &:first-child
    border-top none

.filter-group-header
  cursor pointer
  display flex
  justify-content space-between
  align-items center
  transition background-color 0.2s ease
  h4
    margin 0
    color #7B8DA0
    font-size 14px
    font-weight 600

.filter-group-header-content
  display flex
  align-items center
  gap 8px

.filter-group-header-clear-icon
  width 16px
  height 16px
  display flex
  align-items center
  justify-content center
  font-size 12px
  color #7B8DA0
  cursor pointer
  &:hover
    color invalid-color

.filter-group-content
  padding-top 6px

.accordion-icon
  transition transform 0.2s ease
  color #7B8DA0
  font-size 16px

  &.open
    transform rotate(90deg)

.drawer-footer
  position absolute
  bottom 0
  left 0
  right 0
  padding 16px
  border-top 1px solid #e0e0e0
  background #fff
  display flex
  gap 12px

.apply-button
  flex 1
  height 36px
  background button-background-color
  color #fff
  border none
  cursor pointer
  font-size 14px
  font-weight 500
  transition all 0.2s ease

  &:disabled
    cursor not-allowed

.clear-button
  flex 1
  height 36px
  background #fff
  color #666
  border 1px solid #e0e0e0
  cursor pointer
  font-size 14px
  font-weight 500
  transition all 0.2s ease

  &:enabled:hover
    background #f5f5f5
    border-color #ccc

  &:disabled
    cursor not-allowed
    opacity 0.6

.search-area
  padding 10px
  border-bottom 1px solid #e0e0e0

.search-container
  display flex
  align-items center
  width 100%
  border 1px solid #e0e0e0
  border-radius 4px
  padding-right 8px

  .search-field
    width 100%
    padding 6px 8px
    font-size 13px
    outline none
    border none
    color text-color
    background-color transparent

  .search-icon
    width 16px
    height 16px
    margin-left 8px
    cursor pointer
    opacity 0.7

   .clear-icon
    width 16px
    margin-left 8px
    cursor pointer
    position relative
    opacity 0.7
    color #303030

    &:before, &:after
      content ''
      position absolute
      top 0
      left 50%
      width 100%
      height 2px
      background-color #303030
      transform-origin center
    &:before
      transform translateX(-50%) rotate(45deg)
    &:after
      transform translateX(-50%) rotate(-45deg)
    &:hover
      opacity 1
</style>
